import { ref, watch } from 'vue'
import { useI18n } from '@/composable/useI18n'
import { useSensorStore } from '@/store/sensor'
import { useSuccessStore } from '@/store/success'
import { SensorType } from '@/interfaces/enums'
import * as yup from 'yup'

interface SensorFormData {
  // Basic fields
  name: string
  mac_address: string
  subid: string
  status: string
  // Energy sensor fields
  nominal_voltage: number
  min_utilization_current: number
  min_non_utilization_current: number
  current_calculation_ratio_phase_a: number
  current_calculation_ratio_phase_b: number
  current_calculation_ratio_phase_c: number
  voltage_calculation_ratio: number
  inverted_signal: boolean
  // Door open sensor fields
  voltage_field: string
  // Temperature sensor fields
  temperature_setpoint: number
  temperature_value_divisor: number
}

export function useEditSensorModal() {
  const { t } = useI18n()
  const storeSensor = useSensorStore()
  const successStore = useSuccessStore()

  const errors = ref<{ [key: string]: string | undefined }>({})
  const isLoading = ref(false)
  const sensorType = ref<string>('')

  const formData = ref<SensorFormData>({
    // Basic fields
    name: '',
    mac_address: '',
    subid: '',
    status: 'active',
    // energy sensor fields
    nominal_voltage: 220,
    min_utilization_current: 0,
    min_non_utilization_current: 0,
    current_calculation_ratio_phase_a: 0,
    current_calculation_ratio_phase_b: 0,
    current_calculation_ratio_phase_c: 0,
    voltage_calculation_ratio: 0,
    inverted_signal: false,
    // door open sensor fields
    voltage_field: '',
    // temperature sensor fields
    temperature_setpoint: 0,
    temperature_value_divisor: 10,
  })

  const validationSchema = yup.object().shape({
    name: yup.string().required(t('errors.required', { label: t('sensors.name') })),
    mac_address: yup.string().required(t('errors.required', { label: 'MAC Address' })),
    subid: yup.string().required(t('errors.required', { label: 'SubID' })),
  })

  const validate = async () => {
    errors.value = {}
    try {
      await validationSchema.validate(
        {
          name: formData.value.name,
          mac_address: formData.value.mac_address,
          subid: formData.value.subid,
        },
        { abortEarly: false },
      )
      return true
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        error.inner.forEach((err) => {
          if (err.path) {
            errors.value[err.path] = err.message
          }
        })
      }
      return false
    }
  }

  const loadSensorData = async (sensorId: number) => {
    try {
      const sensor = storeSensor.listSensors.find((s) => s.id === sensorId)
      if (!sensor) return

      sensorType.value = sensor.sensor_type

      const sensorDetails = await storeSensor.fetchSensorDetails(sensorId, sensor.sensor_type)

      if (sensorDetails) {
        formData.value.name = sensorDetails.name || ''
        formData.value.mac_address = sensorDetails.mac_address || ''
        formData.value.subid = sensorDetails.subid || ''
        formData.value.status = sensorDetails.status || 'active'

        if (sensor.sensor_type === SensorType.ELECTRICITY) {
          formData.value.nominal_voltage = sensorDetails.nominal_voltage || 220
          formData.value.min_utilization_current = sensorDetails.min_utilization_current || 0
          formData.value.min_non_utilization_current = sensorDetails.min_non_utilization_current || 0
          formData.value.current_calculation_ratio_phase_a = sensorDetails.current_calculation_ratio_phase_a ?? 0
          formData.value.current_calculation_ratio_phase_b = sensorDetails.current_calculation_ratio_phase_b ?? 0
          formData.value.current_calculation_ratio_phase_c = sensorDetails.current_calculation_ratio_phase_c ?? 0
          formData.value.voltage_calculation_ratio = sensorDetails.voltage_calculation_ratio ?? 0
          formData.value.inverted_signal = Boolean(sensorDetails.inverted_signal)
        } else if (sensor.sensor_type === SensorType.DOOR_OPEN) {
          formData.value.voltage_field = sensorDetails.voltage_field || ''
        } else if (sensor.sensor_type === SensorType.TEMPERATURE) {
          formData.value.temperature_setpoint = sensorDetails.temperature_setpoint || 0
          formData.value.temperature_value_divisor = sensorDetails.temperature_value_divisor ?? 10
        }
      }
    } catch {
      // Erro ao carregar dados do sensor
    }
  }

  const saveSensor = async (sensorId: number, emit: any) => {
    const isValid = await validate()

    if (!isValid) return

    isLoading.value = true

    try {
      const commonData = {
        name: formData.value.name,
        mac_address: formData.value.mac_address,
        subid: formData.value.subid,
        status: formData.value.status,
      }

      if (sensorType.value === SensorType.ELECTRICITY) {
        await storeSensor.updateEnergySensorInfo(sensorId, {
          ...commonData,
          min_utilization_current: formData.value.min_utilization_current,
          min_non_utilization_current: formData.value.min_non_utilization_current,
          nominal_voltage: formData.value.nominal_voltage,
          current_calculation_ratio_phase_a: formData.value.current_calculation_ratio_phase_a || undefined,
          current_calculation_ratio_phase_b: formData.value.current_calculation_ratio_phase_b || undefined,
          current_calculation_ratio_phase_c: formData.value.current_calculation_ratio_phase_c || undefined,
          voltage_calculation_ratio: formData.value.voltage_calculation_ratio || undefined,
          inverted_signal: formData.value.inverted_signal,
        })
      } else if (sensorType.value === SensorType.DOOR_OPEN) {
        await storeSensor.updateDoorOpenSensorInfo(sensorId, {
          ...commonData,
          voltage_field: formData.value.voltage_field,
        })
      } else if (sensorType.value === SensorType.TEMPERATURE) {
        await storeSensor.updateTemperatureSensorInfo(sensorId, {
          ...commonData,
          temperature_setpoint: formData.value.temperature_setpoint,
          temperature_value_divisor: formData.value.temperature_value_divisor,
        })
      }

      successStore.setSuccess([t('globals.changed_with_success')])

      emit('saved', {
        sensorId,
        sensorType: sensorType.value,
        updatedData: formData.value,
      })

      emit('close')
    } catch {
      // Erro ao salvar sensor
    } finally {
      isLoading.value = false
    }
  }

  watch(
    () => [formData.value.name, formData.value.mac_address, formData.value.subid],
    () => {
      errors.value.name = undefined
      errors.value.mac_address = undefined
      errors.value.subid = undefined
    },
  )

  return {
    formData,
    errors,
    isLoading,
    sensorType,
    loadSensorData,
    saveSensor,
    validate,
  }
}
